import ClearIcon from '@mui/icons-material/Clear';
import { Chip, SelectChangeEvent } from '@mui/material';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  filtersTitleStyle,
  sortSelectWrapperStyle,
  sortSelectStyle,
  filtersSectionStyle,
  filtersHeaderStyle,
  filtersResultsStyle,
  filtersResultsValueStyle,
  filtersFormStyle,
  selectedTagsStyle,
  resetButtonStyle,
} from './CepTemplateSelectorFilters.style';
import { SelectOption } from 'basics/options/options.types';
import useCepTemplateSelectorFilters from 'components/CepTemplateSelectorFilters/CepTemplateSelectorFilters.hook';
import { FormLayoutContainer } from 'components/FormLayout';
import ClearAllIcon from 'components/Icons/ClearAllIcon';
import { CustomerEngagementPlanning, SortFilter } from 'generated/maya.types';
import Box from 'yoda-ui/components/Box';
import Select from 'yoda-ui/components/Form/Select';
import MultiSelectYoda from 'yoda-ui/components/Form/SelectYoda/MultiSelectYoda';
import { TagStyle } from 'yoda-ui/components/Form/SelectYoda/MultiSelectYoda/MultiSelectYoda.styles';
import MenuItem from 'yoda-ui/components/MenuItem';
import { YodaFormProvider } from 'yoda-ui/yodaForm';
import { YodaFontSize } from 'yoda-ui/yodaTheme';

type CepTemplateSelectorFiltersPropsType = {
  setCepTemplateSelectorFilters: (value: { channel?: string[] }) => void;
  setCepSortFilter: (value: SortFilter) => void;
  ceps: CustomerEngagementPlanning[];
  count: number;
  loading: boolean;
};

const CepTemplateSelectorFilters: FC<CepTemplateSelectorFiltersPropsType> = ({
  setCepTemplateSelectorFilters,
  setCepSortFilter,
  ceps,
  count,
  loading,
}) => {
  const { t } = useTranslation();

  const {
    providerFields,
    cepTemplateSelectorFiltersConfig,
    handleChannelChange,
    cepSortFieldConfig,
    cepSortFilter,
    handleSortChange,
    selectedFilters,
    handleRemoveChip,
    handleReset,
    hasSelectedFilters,
    hasActiveFilters,
  } = useCepTemplateSelectorFilters({ setCepTemplateSelectorFilters, setCepSortFilter });

  return (
    <Box>
      <YodaFormProvider { ...providerFields } >
        <Box sx={ filtersSectionStyle }>
          <Box sx={ filtersHeaderStyle }>
            <Box sx={ filtersTitleStyle }>{ t('maya_filter_by') }</Box>
            {
              !loading && (
                <Box sx={ filtersResultsStyle }>
                  { t('maya_ci_filter_results_title') }
                  <Box component="span" sx={ filtersResultsValueStyle }>
                    { t('maya_ci_filter_results_value', { shownItems: ceps.length, allItems: count }) }
                  </Box>
                </Box>
              )
            }
          </Box>
          <FormLayoutContainer sx={ filtersFormStyle }>
            <Box>
              <MultiSelectYoda
                { ...cepTemplateSelectorFiltersConfig.channel }
                onChange={ handleChannelChange }
              />
            </Box>
          </FormLayoutContainer>
          {
            hasActiveFilters
            && <Box sx={ selectedTagsStyle }>
              {
                selectedFilters.channel.map((option) => (
                  <Chip
                    key={ option.id }
                    label={ option.label }
                    sx={ TagStyle }
                    onDelete={ () => handleRemoveChip('channel', option.id) }
                    deleteIcon={ <ClearIcon /> }
                  />
                ))
              }
              {
                hasSelectedFilters && (
                  <Box onClick={ handleReset } sx={ resetButtonStyle }>
                    <ClearAllIcon />
                    { t('actions_reset_label') }
                  </Box>
                )
              }
            </Box>
          }
        </Box>
        <Box sx={ sortSelectWrapperStyle }>
          <Select
            value={ cepSortFilter }
            onChange={
              (event: SelectChangeEvent<unknown>, _child: ReactNode) => {
                const selectedOption = cepSortFieldConfig.options.find((opt) => opt.id === event.target.value);
                if (selectedOption) {
                  handleSortChange(selectedOption);
                }
              }
            }
            sx={ sortSelectStyle }
          >
            {
              cepSortFieldConfig.options.map(
                (option: SelectOption) => {
                  return <MenuItem key={ option.id }
                    value={ option.id } sx={ { fontSize: YodaFontSize.small } }>{ option.label }</MenuItem>;
                },
              )
            }
          </Select>
        </Box>
      </YodaFormProvider>
    </Box>
  );
};

export default CepTemplateSelectorFilters;
